import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Check, Star, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/contexts/I18nContext';

interface PurchaseCardProps {
  title: string;
  description?: string;
  price: number;
  currency?: string;
  features: string[];
  popular?: boolean;
  recommended?: boolean;
  buttonText: string;
  onPurchase: () => void;
  disabled?: boolean;
  className?: string;
  isActive?: boolean;
  badge?: string;
  tokenCount?: number;
}

export const PurchaseCard: React.FC<PurchaseCardProps> = ({
  title,
  description,
  price,
  currency = 'USD',
  features,
  popular = false,
  recommended = false,
  buttonText,
  onPurchase,
  disabled = false,
  className,
  isActive = false,
  badge,
  tokenCount
}) => {
  const { t } = useTranslation();
  const currencySymbol = currency === 'USD' ? '$' : currency === 'CNY' ? '¥' : currency;

  // 根据类型选择不同的配色方案
  const getCardStyle = () => {
    if (popular) {
      return {
        gradient: "from-amber-400 via-orange-500 to-red-500",
        cardBg: "bg-gradient-to-br from-amber-50 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20",
        border: "border-amber-200 dark:border-amber-800",
        shadow: "shadow-amber-500/25",
        glow: "shadow-lg shadow-amber-500/25",
        accent: "text-amber-600 dark:text-amber-400"
      };
    }
    if (recommended) {
      return {
        gradient: "from-emerald-400 via-cyan-500 to-blue-500",
        cardBg: "bg-gradient-to-br from-emerald-50 to-cyan-100 dark:from-emerald-900/20 dark:to-cyan-900/20",
        border: "border-emerald-200 dark:border-emerald-800",
        shadow: "shadow-emerald-500/25",
        glow: "shadow-lg shadow-emerald-500/25",
        accent: "text-emerald-600 dark:text-emerald-400"
      };
    }
    if (tokenCount) {
      return {
        gradient: "from-slate-400 via-gray-500 to-slate-600",
        cardBg: "bg-gradient-to-br from-slate-50 to-gray-100 dark:from-slate-900/20 dark:to-gray-900/20",
        border: "border-slate-200 dark:border-slate-700",
        shadow: "shadow-slate-500/20",
        glow: "shadow-lg shadow-slate-500/20",
        accent: "text-slate-600 dark:text-slate-400"
      };
    }
    return {
      gradient: "from-slate-400 via-gray-500 to-zinc-500",
      cardBg: "bg-gradient-to-br from-slate-50 to-gray-100 dark:from-slate-900/20 dark:to-gray-900/20",
      border: "border-slate-200 dark:border-slate-800",
      shadow: "shadow-slate-500/25",
      glow: "shadow-lg shadow-slate-500/25",
      accent: "text-slate-600 dark:text-slate-400"
    };
  };

  const style = getCardStyle();

  return (
    <div className={cn("group relative", className)}>
      {/* 动态发光效果 */}
      <div className={cn(
        "absolute -inset-1 rounded-xl blur-sm opacity-25 group-hover:opacity-75 transition-all duration-500",
        `bg-gradient-to-r ${style.gradient}`
      )}></div>
      
      {/* 主卡片 */}
      <Card className={cn(
        "relative h-full transition-all duration-300 transform hover:scale-105 hover:-translate-y-1",
        style.cardBg,
        style.border,
        style.glow,
        "backdrop-blur-sm",
        isActive && "ring-2 ring-blue-500",
        popular && "ring-2 ring-amber-400",
        recommended && "ring-2 ring-emerald-400"
      )}>
        <div className="p-8 space-y-6">
          {/* 顶部标签 */}
          {(popular || recommended || badge) && (
            <div className="flex items-center justify-between mb-4">
              <div className="flex gap-2 flex-wrap">
                {popular && (
                  <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 font-bold px-3 py-1">
                    <Star className="w-3 h-3 mr-1" />
                    {t('purchase.popular')}
                  </Badge>
                )}
                {recommended && (
                  <Badge className="bg-gradient-to-r from-emerald-500 to-cyan-500 text-white border-0 font-bold px-3 py-1">
                    <Zap className="w-3 h-3 mr-1" />
                    {t('purchase.recommended')}
                  </Badge>
                )}
                {badge && (
                  <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 font-bold px-3 py-1">
                    {badge}
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* 标题和描述 */}
          <div className="space-y-3">
            <h3 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
              {title}
            </h3>
            {description && (
              <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                {description}
              </p>
            )}
          </div>

          {/* 价格 */}
          <div className="space-y-2">
            <div className="flex items-baseline gap-1">
              <span className={cn("text-lg font-medium", style.accent)}>
                {currencySymbol}
              </span>
              <span className="text-5xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                {price}
              </span>
              {!tokenCount && (
                <span className="text-gray-500 dark:text-gray-400 text-lg">{t('purchase.perMonth')}</span>
              )}
            </div>
            
            {tokenCount && (
              <div className={cn("text-xl font-bold", style.accent)}>
                {tokenCount.toLocaleString()} {t('purchase.tokens')}
              </div>
            )}
          </div>

          {/* 特性列表 */}
          <div className="space-y-4">
            <h4 className="text-gray-900 dark:text-white font-semibold text-lg">{t('purchase.features')}</h4>
            <ul className="space-y-3">
              {features.map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className={cn(
                    "rounded-full p-1 mt-0.5 flex-shrink-0",
                    `bg-gradient-to-r ${style.gradient}`
                  )}>
                    <Check className="w-3 h-3 text-white" />
                  </div>
                  <span className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                    {feature}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* 购买按钮 */}
          <Button
            onClick={onPurchase}
            disabled={disabled}
            className={cn(
              "w-full font-bold py-4 text-lg rounded-lg transition-all duration-300 transform hover:scale-105 border-0",
              popular && "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-500/25",
              recommended && "bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white shadow-lg shadow-emerald-500/25",
              tokenCount && "bg-gradient-to-r from-slate-600 to-gray-600 hover:from-slate-700 hover:to-gray-700 text-white shadow-lg shadow-slate-500/20",
              !popular && !recommended && !tokenCount && "bg-gradient-to-r from-slate-600 to-gray-600 hover:from-slate-700 hover:to-gray-700 text-white shadow-lg shadow-slate-500/25",
              disabled && "opacity-50 cursor-not-allowed hover:scale-100 bg-gray-400"
            )}
          >
            {buttonText}
          </Button>
        </div>
      </Card>
    </div>
  );
}; 